
import img0 from './Install_1.png';
import img1 from './Edge_WebView.png';
import img2 from './Download_Error.png';
import img3 from './DotNet_Runtime.png';
import img4 from './DotNet_Runtime_Confirmation.png';
import img5 from './Install_2.png';
import img6 from './Install_3.png';
import img7 from './Login.png';
import img8 from './Connect_Database.png';
import img9 from './Add_Select_Database.png';
import img10 from './Add_Database.png';
import img11 from './Assign_Schemas.png';
import img12 from './Upload_Schema.png';
import img13 from './Schema_Only_Backup.png';
import img14 from './Encryption_Settings.png';
import img15 from './Encryption_In_Progress.png';
import img16 from './Encryption_Completed.png';
import img17 from './Encryption_Failed.png';
  
const content = `# OnData Encryption Tool (Version 2)

The OnData Encryption Tool can be used to efficiently encrypt, decrypt, or obfuscate the sensitive data in databases. It can also be used to apply any changes 
of the encryption settings to a database that runs in a production environment.  

The list of database engines that are currently supported by the OnData Encryption Tool includes:  
- Azure Cosmos DB
- DB2
- MariaDB
- MongoDB
- MySQL
- Oracle
- PostgreSQL
- Snowflake
- SQL Server

## 1. Prerequisites
Here are the prerequisites for using the OnData Encryption Tool.

### 1.1 Operating Systems
- Windows 10 or later, 64-bit edition
- Windows Server 2019 or later, 64-bit edition

### 1.2 Runtimes
To run the Windows version of the OnData Encryption Tool that presents a user interface, the following runtime libraries are required. You may install such runtime 
libraries before installing the OnData Encryption Tool.  
- [Microsoft Edge WebView Runtime](http://go.microsoft.com/fwlink/p/?LinkId=2124703)
- [Microsoft .NET 8.0 Desktop Runtime for Windows x64](https://download.visualstudio.microsoft.com/download/pr/ba3a1364-27d8-472e-a33b-5ce0937728aa/6f9495e5a587406c85af6f93b1c89295/dotnet-sdk-8.0.404-win-x64.exe)

### 1.3 Database Prerequisites
To run the OnData Encryption Tool with certain database engines, some specific configurations and permissions are required. Please refer to the below requirements 
listed for each database engine.

#### 1.3.1 SQL Server
- The TCP/IP network protocol needs to be enabled for the database server. You can confirm such configuration with the \`SQL Server Configuration Manager\`. 
Please refer to [this link](https://docs.microsoft.com/en-us/sql/database-engine/configure-windows/enable-or-disable-a-server-network-protocol?view=sql-server-ver15) 
for more details.

- A SQL Server database user with the following permissions assigned at a minimum:
  - db_datareader
  - db_datawriter
  - db_ddladmin
  - VIEW SERVER STATE

#### 1.3.2 Oracle
- An Oracle database user with the following permissions assigned at a minimum:
  - SELECT_CATALOG_ROLE
  - LOCK ANY TABLE
  - UPDATE ANY TABLE
  - INSERT ANY TABLE
  - SELECT ANY DICTIONARY
  - SELECT ANY TABLE
  - ALTER ANY TABLE
  - CREATE ANY TRIGGER
  - ALTER ANY TRIGGER
  - CREATE SESSION
  - DROP ANY TABLE
  - ALTER ANY MATERIALIZED VIEW
  - CREATE ANY TABLE
  - CREATE ANY INDEX
  - ALTER ANY INDEX
  - DROP ANY INDEX
  - CREATE ANY PROCEDURE
  - SELECT ANY SEQUENCE
  - CREATE ANY SEQUENCE
  - DROP ANY SEQUENCE
  - ALTER ANY SEQUENCE
  - EXECUTE ANY TYPE
  
- The OnData Encryption Tool can fully utilize the parallel processing capability of the database server. If the database server has many CPU cores, you may 
review and update the processes parameter. To view the current processes parameter, use the command: \`show parameter processes\`. If the parameter is low, you 
may increase it to a higher value, for example: \`alter system set processes=2000 scope=spfile\`

#### 1.3.3 MySQL or MariaDB
For MySQL or MariaDB databases, check the local_infile with this query: \`SHOW GLOBAL VARIABLES LIKE 'local_infile';\`
- If the value of the local_infile is set to ON, you don't need to do anything else.
- If the value of the local_infile is set to OFF, it means that data import from the client side is disabled. Please update the my.cnf file (for Mac and Linux operating systems) or the my.ini file (for Windows operating systems) with the following configuration. Once the configuration file is updated, save it and restart your MySQL/MariaDB server.

\`\`\`ini
[mysqld]
local_infile=ON
\`\`\`

Please refer to [this link](https://dev.mysql.com/doc/refman/8.0/en/load-data-local-security.html#load-data-local-configuration) for more details.

#### 1.3.4 MongoDB
- An MongoDB database user with the "readWrite" permissions assigned at a minimum. You may use the following scripts to assign the permssion:

\`\`\`bash
db.grantRolesToUser(
   "{user_name}",
   [{role: "readWrite", db:"{database name}"}]
)
\`\`\`
- There are multiple limitations when processing MongoDB databases. Please refer to 
[MongoDB Encryption Limitations](reference_doc#best_practices_and_limitations/database_product/mongodb_encryption_limitations) for more details.  

### 1.4 OnData Account with Sufficient Permissions
You need to have an active account with the OnData Platform in order to use this tool. The following permissions are required to use OnData Encryption Tool:
- Manage Backup Jobs
- Schedule Encryption and Obfuscation

If you would like to create encryption keys using the OnData Encryption Tool, you will need the following permission. But this is not required. The encryption keys 
can be created by System Administrators and Security Administrators at the OnData Portal.
- Key Management

## 2. Installation
The OnData Encryption Tool comes with a \`setup.exe\` and a Microsoft Windows Installer (MSI) for the Windows platform. After obtaining the package from OnData, 
you can extract it and run the \`setup.exe\` inside to install the application. Please note that administrative access to the operating system is required to install 
the OnData Encryption Tool.  
  
Below is the list of steps to install the application on Windows.  
a. Double click on the \`setup.exe\` after extracting the package. The following screen is displayed.  
  
![Welcome Screen](${ img0 })  
  
**Note**: If your computer does not have the Microsoft Edge WebView runtime installed, it may prompt you to install the Edge WebView
runtime component first, as illustrated below. Please select \`Install\` to install the Edge WebView runtime component from Microsoft.  
  
![Edge WebView](${ img1 })  
  
If your Internet security settings are very restrictive, the automated download of the Edge WebView runtime component may fail. Below is
a sample screenshot for that failure.  
  
![Edge WebView Download Error](${ img2 })  
  
In that case, please visit [this link](http://go.microsoft.com/fwlink/p/?LinkId=2124703) directly to download and install the Edge WebView
runtime component. After that, please click on the "Cancel" button to exit the OnData Encryption Tool Setup, and double click on the
setup.exe again to proceed with the installation.  

**Note**: If your computer does not have the Microsoft .NET Runtime 7.x installed, it may prompt you to install the .NET Desktop Runtime 7.x
component first, as illustrated below. Please select \`Install\` to install the .NET Desktop Runtime 7.x component from Microsoft.  
  
![.NET Desktop Runtime](${ img3 })  
  
The system may prompt you to confirm the installation of the .NET Desktop Runtime. Please select "Yes" to confirm.  
  
![.NET Desktop Runtime Confirmation](${ img4 })  

Once the .NET Desktop Runtime is installed, the installer will continue with the steps below to install the OnData Encryption Tool.

b. Click on Next and select a folder where the application should be installed. It is recommended to use the default path.  
  
![Installation Folder](${ img5 })  

c. Click on Next and Next again to confirm the installation. You will be prompted to allow the installer to make changes to your system. The screen will display OnData as the software publisher.  
  
![Confirmation](${ img6 })  

d. Select Yes to proceed with the installation. Wait till the installation to complete. After the installation is finished,
click on the Close button to exit the installation process. A shortcut to the OnData Encryption Tool is placed on the desktop.

## 3. Registration
If you have not registered your machine as an OnData client, you need to perform the client registration first. For details on
how to register an OnData client, please refer to the [**OnData Client Registration**](reference_doc#how-to_guides/drivers/client_registration).
A copy of the OnData Configurator (configurator.exe) used for the registration is located in the installation folder of the OnData
Encryption Tool, with the default path of \`C:/Program Files/OnData/OnData Encryption Tool/\`. You can launch the OnData Configurator
from the Windows Start menu.  

## 4. Step-by-Step Instructions
After your computer is registered as an OnData client, you can launch the OnData Encryption Tool from either the Desktop shortcut or from the Windows Start menu. 
The OnData Encryption Tool provides a wizard that goes through the following steps:
- Login
- Connect to Database
- Add Database to OnData Portal
- Add or Select Database
- Update Database Schema
- Schema Only Backup
- Encryption Settings
- Operation In Progress
- Operation Completed
  
The instructions for each step are illustrated below.  

**Note**: The database encryption process may take minutes or hours, depending on the size of the database. You may adjust the power 
management settings of your computer so that it will not enter sleep or hibernate mode during processing.  
  
### 4.1 Login
To get started, please click on the \`Login\` button to login to the OnData platform. Use the same credentials as what you would use to login to the 
OnData Portal.  

![Login Screen](${ img7 })  

### 4.2 Connect to Database

Select the database engine and enter the connection information to connect to the database.

![Connect to Database](${ img8 })

There are two options to complete the database connection information:

- \`Use existing\`: Use existing connectivity information of a database that was successfully connected to in the past. The options are sorted by 
database names. You may still need to fill in account credentials because such credentials are not cached for security purpose.
- \`Create new\`: Create a new entry of database connectivity information. Once connected successfully, the database connectivity information will be 
cached locally for future use. If connectivity information for a database is cached before, such connectivity information will be updated. 

**Note**: Any password associated with the database connectivity will never be cached for security purpose.

If you choose to create a new entry of database connectivity information, you may select the following options based on the database type:
- Azure Cosmos DB
  - Database Name: The name of the database to connect to
  - Endpoint URL: In the \`Settings\` section of the Azure Cosmos DB account, select \`Keys\`, then select "Copy" to copy the \`URI\` to this field
  - Authorization Key: In the \`Settings\` section of the Azure Cosmos DB account, select \`Keys\`, then select "Copy" to copy the \`PRIMARY KEY\` to this field
- DB2
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the DB2 database server
  - Port: The Port number for the DB2 database server. The default is 25000
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- MariaDB
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the MariaDB database server
  - Port: The Port number for the MariaDB database server. The default is 3306
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- MongoDB
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the MongoDB database server
  - Port: The Port number for the MongoDB database server. The default is 27017
  - Database Name: The name of the database to connect to
  - User Type: Whether the user is a Global User or a Database User 
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- MySQL
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the MySQL database server
  - Port: The Port number for the MySQL database server. The default is 3306
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- Oracle  
  There is an option to use TNS or not for Oracle. If TNS is used, fill in the following information:
  - TNS ADMIN: A directory where the SQL*Net configuration files (sqlnet.ora and tnsnames.ora) are located. This is optional. If left empty, it will 
  use the TNS_ADMIN environment variable to find the configuration files
  - TNS Service Name: The name of the TNS service for connecting to the database server
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
  
  If TNS is not used, fill in the following information:
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the Oracle database server
  - Port: The Port number for the Oracle database server. The default is 1521
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- PostgreSQL
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the PostgreSQL database server
  - Port: The Port number for the PostgreSQL database server. The default is 5432
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- Snowflake
  - Host: The hostname of your Snowflake account, e.g. &lt;ACCOUNT&gt;.snowflakecomputing.com
  - Warehouse: The Snowflake Warehouse used for queries
  - Database Name: The name of the database to connect to
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
- SQL Server
  - Server Address: The IP address or Fully Qualified Domain Name (FQDN) of the MS SQL database server
  - Port: The Port number for the MS SQL database server. The default is 1433
  - Database Name: The name of the database to connect to
  
  If \`Integrated Security\` is checked, the security context of the current logon user will be used to connect to the SQL database. If \`Integrated Security\` 
  is unchecked, you will need to provide the following account credentials to connect to the MS SQL database:
  - Username: The username used to connect to the database. The user must have sufficient read and write permissions to the database
  - Password: The password for the database user
  
After you fill in the database connectivity information, you may select the \`Test\` button to test the database connectivity and update the 
connectivity information if the database cannot be connected.

Select \`Connect\` to connect to the database. Depending on whether database is already managed by OnData, you may be redirected to different pages:
  - If the database does not exist in OnData Portal, it will show the \`Add Database to OnData Portal\` page
  - If the database exists in OnData Portal, it will show the \`Update Database Schema\` page. **Note**: For an Oracle database, the tool will further check if the physical database name matches the logical database name used at the OnData Portal. If they do not match, the tool will show 
  the \`Add or Select Database\` page. 
  
![Add or Select Database](${ img9 })  
  
This scenario happens only when you have chosen to include only a subset of the database schemas from the physical database before. In this case, 
you are presented with two options:
  - Choose the \`Select Database\` option and select a previously created logic database from the \`OnData Database Name\` dropdown list and proceed to 
  the next steps
  - Choose the \`Add Database\` option and click on the \`Add\` button to add another logical database to the OnData Portal by selecting a different 
  subset of database schemas from the same physical database
  
### 4.3 Add Database to OnData Portal
This page allows you to add the database to the OnData Portal. You may select an Encryption Key and enter a description of the database. If your OnData 
account has the permissions to manage encryption keys, this page will allow you to create a new encryption key as needed. Clicking on the \`Add\` button 
to add the database to the OnData Portal. The tool will transition to the \`Upload Database Schema\` page.
  
![Add Database](${ img10 })  
  
**Note**: If the database engine is Oracle, you have the option of processing a subset of the schemas from the physical Oracle database. In that case, you can check 
the option of \`Specify Subset of Schemas\`. The OnData Encryption Tool will ask for a logical database name for the OnData Portal to represent this subset of 
schemas. Please fill in the logical database name in the \`OnData Database Name\` field, and assign a subset of schemas to this logical database. Clicking on the \`Add\` button 
to add the database to the OnData Portal. The tool will transition to the \`Upload Database Schema\` page.
  
![Assign Schemas](${ img11 })  
  
### 4.4 Upload Database Schema
This page displays a summary about the database and the encryption key to be used. You may choose any of the following options:
- Cancel: Go back to the \`Connect to Database\` page
- Upload: Go to the \`Schema Only Backup\` page and perform a schema-only backup for the database
    
![Upload Schema](${ img12 })  
  
Please note that when the database schema is captured and uploaded **the first time and the first time only**, OnData will automatically detect the sensitive 
info types that are configured and enabled under \`Settings -> Encryption -> Data Info Types\`. Any data fields matching those sensitive info types will be 
automatically classified and assigned with an encryption rule, based on the configuration of the corresponding info types.
  
### 4.5 Update Database Schema
This page shows information about the existing database, including the assigned Encryption Key, and allows you to update the database schema if 
preferred. You may choose any of the following options:
- Skip: Skip the schema-only backup step and go to the \`Encryption Settings\` page. Please choose this option if you are sure that the database 
schema has not been changed since last backup
- Cancel: Go back to the \`Connect to Database\` page
- Update: Go to the \`Schema Only Backup\` page and perform a schema-only backup for the database

### 4.6 Schema Only Backup
The schema-only backup process will be performed, with the relevant messages displayed inside the \`Status Message\` box. After the schema-only backup 
process is completed, you may choose any of the following options:
- Cancel: Go back to the \`Connect to Database\` page
- Next: Go to the \`Encryption Settings\` page
  
![Schema Only Backup](${ img13 })  
  
### 4.7 Encryption Settings
This page provides a direct link to the OnData Portal so that you can configure the encryption rules for the database. You may be required to login 
to the OnData Portal first. After the encryption rules are configured for all tables are columns that need to be protected, you may proceed with the 
processing of the database. 
  
![Encryption Settings](${ img14 })  
  
There are three operating modes for the OnData Encryption Tool:
- **Encryption**: This mode is used to encrypt the sensitive data in the database based on the encryption rules configured at the OnData Portal. Please 
note that the database must not be encrypted already. When the Encryption mode is used, and you are using this mode to generate test data, you may 
further choose the following two options:
  - Apply Obfuscation Rules: If an obfuscation rule is configured for a column, the obfuscation rule will be applied instead of applying encryption
  - Subsetting Enabled: If you would like to enable the subsetting feature when generating test data, you may choose this option. In that case, you 
  will need to provide a database name to store the data subset from the original database. A link to the OnData Portal is provided for you to 
  view and update the subsetting configurations
  
    **Warning**: Please do not select \`Apply Obfuscation Rules\` or \`Subsetting Enabled\` if you are encrypting a production database. 
  
- **Decryption**: This mode is used to decrypt the previously encrypted data in the database. Please note that the database must be encrypted before.
- **Apply Encryption Setting Changes**: This mode is used to apply the encryption setting changes to a database in Non-Production or Production environment. 

You may take any of the following actions on this page:
- \`Advanced Options\`: Allow to configure the advanced processing options, including threading, logging, and error handling. Please stay with the default 
options if you are not sure about the changes
  - \`Page Size\`: The number of rows to process for each processing chunk. Please note that for MongoDB, the page size is set to 100 and cannot be changed
  - \`Max Parallelism\`: The maximum number of concurrent tasks for the application. This is used for performance optimization
  - \`Threading Factor\`: The number of threads to start for each logical processor of the computer. This is used for performance optimization as well
  - \`DLL Log Level\`: The log level for the EncryptSQL module
  
  - For relational databases, the following additional options are available:  
    - \`Skip Invalid Input\`: If this option is checked, the OnData Encryption Tool will skip any errors and continue the processing without being interrupted 
  by errors
    - \`Log Error Data\`: If this option is checked, the error data will be logged to the log file. Please note that the error data may contain sensitive data. 
  You should only use this option for debugging purpose and delete the log files after you are done with debugging

  - For MongoDB databases, the following additional options are available:
    - \`Wait Queue Size\`: The maximum number of operations that can be queued up waiting to be executed on a particular MongoDB server or replica set. When the 
  number of incoming requests exceeds the available resources (such as the number of connections or the capacity to handle concurrent operations), these 
  requests are placed in a wait queue
    - \`Wait Queue Timeout\`: The maximum amount of time an operation will wait in the queue before it times out. When an operation is placed in the wait 
  queue due to resource limitations (such as all available connections being used), it will wait for a certain period specified by the wait queue 
  timeout setting. If the operation doesn't get a chance to execute within this timeout period, an error will be thrown
  
- \`Cancel\`: Go back to the \`Connect to Database\` page
- \`Proceed\`: Display a confirmation message to confirm the processing of the database. It is recommended that you have a backup of the database 
before proceeding with the operation. Click on the \`Confirm\` button to proceed to the next step. Depending on the selected operating mode, the 
tool will proceed to the \`Encryption In Progress\` page, the \`Decryption In Progress\` page, or the \`Apply Encryption Setting Changes\` page.
  
Please note that this step may take minutes or hours depending on the size of the database, number of columns and rows to be processed, and 
available hardware resources. You may adjust the power management settings of your computer so that it will not enter sleep or hibernate mode 
during processing.    
  
![Encryption In Progress](${ img15 })  
  
The \`Encryption In Progress\` page, the \`Decryption In Progress\` page, and the \`Apply Encryption Setting Changes\` page will show the relevant 
messages as the database is processed.  Once the processing is completed, the \`Done\` button 
is displayed at the bottom. A \`View Logs\` link is displayed on the page as well, so that you can click on it and go directly to the logs 
folder.
  
![Encryption Completed](${ img16 })  
  
## 5. Recover from Failure
If the above process fails, an error message will be displayed to indicate the reason for the failure. You may review the detailed error messages in the 
log files by following the \`View Logs\` link.
  
![Failed on Encryption](${ img17 })  
  
Once you find the root cause and resolve the issue, you can click on the \`Rerun\` button to resume from where the process has just stopped, without 
starting over the entire process. This option will save a lot of time if your database size is large. If the process fails again, you may use 
the \`Advanced Options\` to turn on more logging and/or skip invalid input if desired.  

Please note that this \`Rerun\` feature only works for relational databases, not for MongoDB databases.  
  `
  
export default content;
